import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

const ExecutionHistory = () => {
  useAuth(); // Verify authentication
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [executionHistory, setExecutionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchExecutionHistory();
  }, []);

  const fetchExecutionHistory = async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const response = await fetch(`${apiUrl}/search-execution/batches`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setExecutionHistory(data.batches || []);
      } else {
        throw new Error('Failed to fetch execution history');
      }
    } catch (error) {
      console.error('Failed to fetch execution history:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBatch = async (batchId) => {
    try {
      setDeleting(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      
      const response = await fetch(`${apiUrl}/search-execution/batches/${batchId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setExecutionHistory(prev => prev.filter(batch => batch.id !== batchId));
        setDeleteConfirm(null);
        console.log(result.message);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete batch');
      }
    } catch (error) {
      console.error('Failed to delete batch:', error);
      setError(`Failed to delete batch: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const locale = i18n.language === 'he' ? 'he-IL' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <p className="text-sm text-gray-600 dark:text-[#c4c4c4]">{t('dashboard.history.loading') || 'Loading execution history...'}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8">
        <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 max-w-md w-full shadow-sm">
          <div className="text-red-600 dark:text-red-300 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-base text-red-600 dark:text-red-300 mb-2 text-center">{t('dashboard.history.loadError') || 'Failed to load history'}</h3>
          <p className="text-sm text-red-500 text-center">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-[#f5f5f5]">
            {t('dashboard.history.title') || 'Execution History'}
          </h1>
          <p className="text-sm mt-2 text-gray-600 dark:text-[#c4c4c4]">
            {t('dashboard.history.subtitle') || 'View and manage your search analysis history'}
          </p>
        </div>
        <span className="bg-white dark:bg-[#1f1f1f] text-sm px-4 py-2 rounded-lg text-gray-700 dark:text-[#c4c4c4] shadow-sm">
          {t('dashboard.history.batchCount', { count: executionHistory.length }) || `${executionHistory.length} batch${executionHistory.length !== 1 ? 'es' : ''} executed`}
        </span>
      </div>

      {/* Execution History List */}
      {executionHistory.length === 0 ? (
        <div className="bg-white dark:bg-[#1f1f1f] rounded-xl p-12 text-center shadow-sm">
          <div className="bg-gray-100 dark:bg-[#262626] rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-gray-400 dark:text-[#999999]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-base mb-2 text-gray-900 dark:text-[#f5f5f5]">{t('dashboard.history.noHistory') || 'No executions yet'}</h3>
          <p className="text-sm mb-6 text-gray-600 dark:text-[#c4c4c4]">
            {t('dashboard.history.noHistoryMessage') || 'Execute search templates to see analysis results here.'}
          </p>
          <button
            onClick={() => navigate('/search')}
            className="bg-[#d8f9b8] text-[#1a1d1f] px-6 py-3 rounded-lg font-medium hover:bg-[#c8e9a8] transition-colors"
          >
            {t('dashboard.history.goToSearch') || 'Go to Search'}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {executionHistory.map((batch) => (
            <div key={batch.id} className="bg-white dark:bg-[#1f1f1f] rounded-xl p-6 hover:bg-gray-50 dark:hover:bg-[#262626] transition-all shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <h4 className="text-base font-medium text-gray-900 dark:text-[#f5f5f5]">
                      {batch.name}
                    </h4>
                  </div>
                  {formatDate(batch.createdAt) && (
                    <p className="text-xs text-gray-600 dark:text-[#c4c4c4]">
                      {formatDate(batch.createdAt)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {batch.status === 'completed' && (
                    <button
                      onClick={() => navigate(`/results/${batch.id}`)}
                      className="bg-[#d8f9b8] text-[#1a1d1f] text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#c8e9a8] transition-colors"
                    >
                      {t('dashboard.history.viewResults') || 'View Results'}
                    </button>
                  )}
                  <button
                    onClick={() => setDeleteConfirm({ batchId: batch.id, batchName: batch.name })}
                    disabled={batch.status === 'running'}
                    className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                    title={batch.status === 'running' ? t('dashboard.history.cannotDeleteRunning') : t('dashboard.history.deleteTooltip')}
                  >
                    {t('dashboard.history.delete') || 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog - Rendered via Portal */}
      {deleteConfirm && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white dark:bg-[#1f1f1f] rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-[#f5f5f5]">
              {t('dashboard.history.deleteTitle') || 'Delete Analysis'}
            </h3>
            <p className="text-sm leading-relaxed mb-6 text-gray-600 dark:text-[#c4c4c4]">
              {t('dashboard.history.deleteConfirmMessage', { name: deleteConfirm.batchName }) ||
                `Are you sure you want to delete the analysis "${deleteConfirm.batchName}"? This will permanently remove all execution results, competitor data, and individual records. This action cannot be undone.`}
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="bg-gray-100 dark:bg-[#262626] text-gray-700 dark:text-[#c4c4c4] px-6 py-3 rounded-lg font-medium disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-[#2e2e2e] transition-colors"
              >
                {t('common.buttons.cancel') || 'Cancel'}
              </button>
              <button
                onClick={() => handleDeleteBatch(deleteConfirm.batchId)}
                disabled={deleting}
                className="bg-red-600 text-white px-6 py-3 rounded-lg font-medium disabled:opacity-50 hover:bg-red-700 transition-colors"
              >
                {deleting ? (
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    {t('dashboard.history.deleting') || 'Deleting...'}
                  </div>
                ) : (
                  t('common.buttons.delete') || 'Delete'
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ExecutionHistory;